import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TrendingUp, ShoppingBag, DollarSign, ArrowLeft } from "lucide-react";

export default function SellerSales() {
  const [sales, setSales] = useState(null);

  useEffect(() => {
    api.get("/seller/sales").then(r => setSales(r.data || []));
  }, []);

  const stats = useMemo(() => {
    if (!sales) return { revenue: 0, units: 0, orders: 0 };
    const revenue = sales.reduce((s, x) => s + Number(x.subtotal || 0), 0);
    const units = sales.reduce((s, x) => s + Number(x.qty || 0), 0);
    const orders = new Set(sales.map(x => x.order_id)).size;
    return { revenue, units, orders };
  }, [sales]);

  if (sales === null) return <div className="pt-32 text-center text-zinc-500" data-testid="sales-loading">Loading…</div>;

  return (
    <div data-testid="seller-sales-page" className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/seller" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-2 mb-6" data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Seller</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 mb-10">Sales</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
        <div className="card-surface p-6">
          <DollarSign className="w-4 h-4 text-[#CCFF00] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Revenue</p>
          <p data-testid="stat-revenue" className="font-display font-black text-3xl mt-1 text-[#CCFF00]">${stats.revenue.toFixed(2)}</p>
        </div>
        <div className="card-surface p-6">
          <ShoppingBag className="w-4 h-4 text-[#FF007F] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Units sold</p>
          <p data-testid="stat-units" className="font-display font-black text-3xl mt-1">{stats.units}</p>
        </div>
        <div className="card-surface p-6">
          <TrendingUp className="w-4 h-4 text-[#05D9E8] mb-3" />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Orders</p>
          <p data-testid="stat-orders" className="font-display font-black text-3xl mt-1">{stats.orders}</p>
        </div>
      </div>

      {sales.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="sales-empty">
          <ShoppingBag className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">No sales yet. Boost a listing to get discovered faster.</p>
          <div className="flex justify-center gap-3 mt-6">
            <Link to="/seller" className="btn-secondary">Back to dashboard</Link>
            <Link to="/seller/new" className="btn-primary">Add a product</Link>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">
                <th className="p-4">When</th>
                <th className="p-4">Order</th>
                <th className="p-4">Product</th>
                <th className="p-4">Qty</th>
                <th className="p-4 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s, i) => (
                <tr key={`${s.order_id}-${s.product_id}-${i}`} data-testid={`sale-row-${s.order_id}-${s.product_id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 text-zinc-400">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="p-4 font-mono text-xs text-zinc-400">{s.order_id.slice(0, 8).toUpperCase()}</td>
                  <td className="p-4">
                    <Link to={`/product/${s.product_id}`} className="font-bold hover:text-[#FF007F]">{s.title}</Link>
                  </td>
                  <td className="p-4">{s.qty}</td>
                  <td className="p-4 text-right font-display font-bold text-[#FF007F]">${Number(s.subtotal).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
